import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Chip } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import NotificationResponseModal from './NotificationResponseModal';
import notificationStore from '../../stores/notificationStore';

// Cards no longer overlap, so more can be shown at once; the list scrolls past this.
const MAX_VISIBLE = 5;
const STORAGE_KEY = 'notif_stack_position';
const CARD_GAP = 10;

function loadSavedPosition() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const pos = JSON.parse(raw);
      if (typeof pos.x === 'number' && typeof pos.y === 'number') return pos;
    }
  } catch (_) {}
  return null;
}

function savePosition(pos) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
  } catch (_) {}
}

/**
 * NotificationResponseStack
 *
 * Draggable, fixed-position list of notification cards. Position is persisted to
 * localStorage. Cards do not overlap: several notifications can be waiting at once
 * and each one has to be answerable without moving the others out of the way.
 */
const NotificationResponseStack = () => {
  const [actionable, setActionable] = useState([]);
  const saved = loadSavedPosition();
  const [position, setPosition] = useState(saved || { x: 0, y: 0 });
  const [hasCustomPos, setHasCustomPos] = useState(!!saved);
  const dragRef = useRef(null);
  const isDragging = useRef(false);
  const dragStart = useRef({ mx: 0, my: 0, px: 0, py: 0 });

  useEffect(() => {
    setActionable(notificationStore.getActionableNotifications());
    const unsubscribe = notificationStore.subscribe(() => {
      setActionable(notificationStore.getActionableNotifications());
    });
    return unsubscribe;
  }, []);

  const handleDismiss = useCallback((id) => {
    notificationStore.dismissModal(id);
  }, []);

  const onPointerDown = useCallback((e) => {
    if (e.target.closest('button') || e.target.closest('[role="button"]')) return;
    const rect = dragRef.current?.getBoundingClientRect();
    if (!rect) return;
    isDragging.current = true;
    dragStart.current = {
      mx: e.clientX,
      my: e.clientY,
      px: rect.left,
      py: rect.top,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e) => {
    if (!isDragging.current) return;
    const dx = e.clientX - dragStart.current.mx;
    const dy = e.clientY - dragStart.current.my;
    const newX = dragStart.current.px + dx;
    const newY = dragStart.current.py + dy;
    setPosition({ x: newX, y: newY });
    setHasCustomPos(true);
  }, []);

  const onPointerUp = useCallback((e) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
    // Save final position
    const rect = dragRef.current?.getBoundingClientRect();
    if (rect) {
      savePosition({ x: rect.left, y: rect.top });
    }
  }, []);

  if (actionable.length === 0) return null;

  const visible = actionable.slice(0, MAX_VISIBLE);
  const hiddenCount = Math.max(0, actionable.length - MAX_VISIBLE);

  // Position style: if user has dragged, use absolute left/top; otherwise default bottom-center
  const positionStyle = hasCustomPos
    ? {
        position: 'fixed',
        left: position.x,
        top: position.y,
        bottom: 'auto',
        transform: 'none',
      }
    : {
        position: 'fixed',
        bottom: 32,
        left: '50%',
        transform: 'translateX(-50%)',
      };

  return (
    <Box
      ref={dragRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      sx={{
        ...positionStyle,
        zIndex: 1500,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        cursor: 'grab',
        '&:active': { cursor: 'grabbing' },
        touchAction: 'none',
        userSelect: 'none',
      }}
      data-testid="notification-response-stack"
    >
      {/* Drag handle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 0.5,
          opacity: 0.4,
          '&:hover': { opacity: 0.7 },
          transition: 'opacity 0.15s',
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: 18, color: 'grey.500', transform: 'rotate(90deg)' }} />
      </Box>

      {/* One card per notification, laid out so each can be read and answered */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: `${CARD_GAP}px`,
          maxHeight: 'calc(100vh - 96px)',
          overflowY: 'auto',
          pointerEvents: 'auto',
        }}
      >
        {visible.map((notification) => (
          <NotificationResponseModal
            key={notification.id}
            notification={notification}
            onDismiss={handleDismiss}
          />
        ))}
      </Box>

      {/* Hidden count indicator */}
      {hiddenCount > 0 && (
        <Chip
          label={`+${hiddenCount} more notification${hiddenCount === 1 ? '' : 's'}`}
          size="small"
          variant="outlined"
          sx={{
            mt: 1,
            pointerEvents: 'auto',
            bgcolor: 'background.paper',
            fontSize: '0.75rem',
          }}
        />
      )}
    </Box>
  );
};

export default NotificationResponseStack;
